import {
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Coins,
  Download,
  HandCoins,
  Hash,
  Search,
  TriangleAlert,
} from 'lucide-react';
import { Fragment, useState } from 'react';

import { BarChart } from '@/components/charts/bar-chart';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { exportarCsv } from '@/lib/export';
import {
  cn,
  formatCurrencyAdaptive,
  formatDateValue,
  formatDateTimeValue,
  formatHoraValue,
  formatNumber,
  toNumber,
} from '@/lib/utils';

import type { PorTurnoAutoservicio } from '../autoservicio.types';
import {
  etiquetaTurnoEstado,
  mensajeAutoservicio,
  turnoEnCurso,
  varianteTurnoEstado,
} from '../autoservicio-format';
import { KpiAutoservicio } from './kpi-autoservicio';

interface PanelPorTurnoProps {
  datos: PorTurnoAutoservicio | undefined;
  cargando: boolean;
  error: unknown;
}

/** Fila de barra proporcional para el resumen de documentación. */
function BarraResumen({
  etiqueta,
  valor,
  total,
  tono,
}: {
  etiqueta: string;
  valor: number;
  total: number;
  tono: string;
}) {
  const porcentaje = total > 0 ? Math.min(100, (valor / total) * 100) : 0;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-ink-700">{etiqueta}</span>
        <span className="font-mono font-semibold text-ink-900 tabular-nums">{formatNumber(valor)}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
        <span className={cn('block h-full rounded-full', tono)} style={{ width: `${porcentaje}%` }} />
      </div>
    </div>
  );
}

/**
 * Pantalla **Operación por turno**.
 *
 * Vista de los mismos agregados del dashboard, una fila por turno, con gráfico
 * comparativo, resumen de documentación, búsqueda, filtro de incidencias, ficha
 * desplegable por turno y exportación CSV.
 */
export function PanelPorTurno({ datos, cargando, error }: PanelPorTurnoProps) {
  const [busqueda, setBusqueda] = useState('');
  const [soloIncidencias, setSoloIncidencias] = useState(false);
  const [expandidos, setExpandidos] = useState<ReadonlySet<number>>(new Set());

  if (error) {
    return (
      <Alert variant="error" title="No se pudieron cargar los turnos">
        {mensajeAutoservicio(error)}
      </Alert>
    );
  }

  const turnos = datos?.turnos ?? [];

  const totalRegistros = turnos.reduce((suma, turno) => suma + toNumber(turno.registros), 0);
  const totalDocumentadas = turnos.reduce((suma, turno) => suma + toNumber(turno.documentadas), 0);
  const totalCobrado = turnos.reduce((suma, turno) => suma + toNumber(turno.montoCobrado), 0);
  const totalSaldo = turnos.reduce((suma, turno) => suma + toNumber(turno.saldoAplicado), 0);
  const totalPendientes = turnos.reduce((suma, turno) => suma + toNumber(turno.pendientesDocumento), 0);
  const totalErrores = turnos.reduce((suma, turno) => suma + toNumber(turno.errores), 0);
  const totalSinDespacho = turnos.reduce((suma, turno) => suma + toNumber(turno.sinDespacho), 0);

  const consulta = busqueda.trim();
  const filas = turnos.filter((turno) => {
    if (soloIncidencias && toNumber(turno.pendientesDocumento) + toNumber(turno.errores) === 0) {
      return false;
    }

    if (consulta === '') return true;

    return String(turno.turnoId).includes(consulta);
  });

  const alternar = (turnoId: number) => {
    setExpandidos((anterior) => {
      const siguiente = new Set(anterior);

      if (siguiente.has(turnoId)) siguiente.delete(turnoId);
      else siguiente.add(turnoId);

      return siguiente;
    });
  };

  const exportar = () => {
    exportarCsv(
      'autoservicio-por-turno',
      [
        'Turno',
        'Llegada',
        'Salida',
        'Estado',
        'Registros',
        'Documentadas',
        'Pendientes',
        'Errores',
        'Sin despacho',
        'Boletas',
        'Facturas',
        'Con código de saldo',
        'Presupuestado',
        'Cobrado',
        'Saldo aplicado',
      ],
      filas.map((turno) => [
        turno.turnoId,
        formatDateTimeValue(turno.hora_llegada),
        turno.hora_salida === null ? 'En curso' : formatDateTimeValue(turno.hora_salida),
        etiquetaTurnoEstado(turno.turno_estado),
        turno.registros,
        turno.documentadas,
        turno.pendientesDocumento,
        turno.errores,
        turno.sinDespacho,
        turno.boletas,
        turno.facturas,
        turno.conCodigoSaldo,
        toNumber(turno.montoPresupuestado),
        toNumber(turno.montoCobrado),
        toNumber(turno.saldoAplicado),
      ]),
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <KpiAutoservicio
          label="Turnos"
          value={formatNumber(turnos.length)}
          hint={`${formatNumber(totalRegistros)} registros`}
          icon={Hash}
          tono="brand"
          cargando={cargando && !datos}
        />
        <KpiAutoservicio
          label="Documentadas"
          value={formatNumber(totalDocumentadas)}
          hint={`${totalRegistros > 0 ? ((totalDocumentadas / totalRegistros) * 100).toFixed(1) : '0.0'} % de los registros`}
          icon={ClipboardList}
          tono="emerald"
          cargando={cargando && !datos}
        />
        <KpiAutoservicio
          label="Pendientes / errores"
          value={`${formatNumber(totalPendientes)} / ${formatNumber(totalErrores)}`}
          hint={`${formatNumber(totalSinDespacho)} sin despacho`}
          icon={TriangleAlert}
          tono={totalErrores > 0 ? 'rose' : 'amber'}
          cargando={cargando && !datos}
        />
        <KpiAutoservicio
          label="Monto cobrado"
          value={formatCurrencyAdaptive(totalCobrado)}
          icon={Coins}
          tono="accent"
          cargando={cargando && !datos}
        />
        <KpiAutoservicio
          label="Saldo aplicado"
          value={formatCurrencyAdaptive(totalSaldo)}
          icon={HandCoins}
          tono="amber"
          cargando={cargando && !datos}
        />
      </div>

      {turnos.length > 0 ? (
        <div className="grid items-stretch gap-4 xl:grid-cols-3">
          <SectionCard
            className="xl:col-span-2"
            title="Cobrado vs presupuestado por turno"
            description="Montos del período, del turno más reciente al más antiguo"
          >
            <BarChart
              labels={turnos.map((turno) => `#${turno.turnoId}`)}
              series={[
                {
                  label: 'Presupuestado',
                  data: turnos.map((turno) => toNumber(turno.montoPresupuestado)),
                },
                { label: 'Cobrado', data: turnos.map((turno) => toNumber(turno.montoCobrado)) },
              ]}
              format="currency"
              height={300}
            />
          </SectionCard>

          <SectionCard title="Documentación de los turnos" description="Totales del período">
            <div className="space-y-4">
              <BarraResumen
                etiqueta="Documentadas"
                valor={totalDocumentadas}
                total={totalRegistros}
                tono="bg-emerald-500"
              />
              <BarraResumen
                etiqueta="Pendientes de documento"
                valor={totalPendientes}
                total={totalRegistros}
                tono="bg-amber-500"
              />
              <BarraResumen
                etiqueta="Errores"
                valor={totalErrores}
                total={totalRegistros}
                tono="bg-rose-500"
              />
              <BarraResumen
                etiqueta="Sin despacho"
                valor={totalSinDespacho}
                total={totalRegistros}
                tono="bg-ink-400"
              />

              <p className="border-t border-ink-100 pt-3 text-[11px] text-ink-400">
                Base: {formatNumber(totalRegistros)} registros en {formatNumber(turnos.length)} turno(s).
              </p>
            </div>
          </SectionCard>
        </div>
      ) : null}

      <SectionCard
        title="Turnos con operaciones"
        description="Búsqueda por número de turno y filtro de incidencias"
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={exportar}
            disabled={filas.length === 0}
            leftIcon={<Download className="size-3.5" aria-hidden="true" />}
          >
            Exportar
          </Button>
        }
      >
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="w-48">
            <Input
              label="Buscar turno"
              placeholder="Ej. 232"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
              leftIcon={<Search className="size-4" aria-hidden="true" />}
            />
          </div>

          <label className="flex h-12 cursor-pointer items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={soloIncidencias}
              onChange={(evento) => setSoloIncidencias(evento.target.checked)}
              className="size-4 rounded border-ink-300 accent-brand-500"
            />
            Sólo con pendientes o errores
          </label>

          <span className="pb-3 text-[11px] text-ink-400">
            {formatNumber(filas.length)} de {formatNumber(turnos.length)} turno(s)
          </span>
        </div>

        {cargando && !datos ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((fila) => (
              <Skeleton key={fila} className="h-11 w-full" />
            ))}
          </div>
        ) : filas.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-500">
            {turnos.length === 0
              ? 'No hay turnos con operaciones de autoservicio en el período.'
              : 'Ningún turno coincide con el filtro.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                  <th className="w-[24%] pb-2 pr-2">Turno</th>
                  <th className="w-[12%] pb-2 pr-2">Estado</th>
                  <th className="w-[18%] pb-2 pr-2 text-right">Registros</th>
                  <th className="w-[14%] pb-2 pr-2 text-right">Comprob.</th>
                  <th className="w-[14%] pb-2 pr-2 text-right">Código saldo</th>
                  <th className="w-[18%] pb-2 text-right">Cobrado</th>
                </tr>
              </thead>

              <tbody>
                {filas.map((turno) => {
                  const enCurso = turnoEnCurso(turno);
                  const abierto = expandidos.has(turno.turnoId);

                  return (
                    <Fragment key={turno.turnoId}>
                      <tr className="border-b border-ink-50 last:border-0">
                        <td className="py-3 pr-2 align-top">
                          <div className="flex items-start gap-1">
                            <button
                              type="button"
                              onClick={() => alternar(turno.turnoId)}
                              aria-expanded={abierto}
                              aria-label={`${abierto ? 'Ocultar' : 'Ver'} detalle del turno ${turno.turnoId}`}
                              className="mt-0.5 cursor-pointer rounded p-0.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                            >
                              {abierto ? (
                                <ChevronDown className="size-4" aria-hidden="true" />
                              ) : (
                                <ChevronRight className="size-4" aria-hidden="true" />
                              )}
                            </button>

                            <div className="min-w-0">
                              <p className="font-mono text-sm font-semibold text-ink-900 tabular-nums">
                                #{turno.turnoId}
                              </p>
                              <p className="mt-0.5 truncate text-[11px] text-ink-400">
                                {formatDateValue(turno.hora_llegada)} ·{' '}
                                {formatHoraValue(turno.hora_llegada)}
                                {enCurso ? ' → en curso' : ` → ${formatHoraValue(turno.hora_salida)}`}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="py-3 pr-2 align-top">
                          <div className="flex flex-col items-start gap-1">
                            <Badge variant={enCurso ? 'brand' : 'neutral'}>
                              {enCurso ? 'En curso' : 'Cerrado'}
                            </Badge>
                            <Badge
                              variant={varianteTurnoEstado(turno.turno_estado)}
                              title="Valor crudo de turno.estado"
                            >
                              {etiquetaTurnoEstado(turno.turno_estado)}
                            </Badge>
                          </div>
                        </td>

                        <td className="py-3 pr-2 text-right align-top">
                          <p className="font-mono text-sm text-ink-900 tabular-nums">
                            {formatNumber(turno.registros)}
                          </p>
                          <p
                            className="mt-0.5 truncate text-[11px] text-ink-400"
                            title={`${formatNumber(turno.documentadas)} documentadas · ${formatNumber(
                              turno.pendientesDocumento,
                            )} pendientes · ${formatNumber(turno.errores)} errores · ${formatNumber(
                              turno.sinDespacho,
                            )} sin despacho`}
                          >
                            {formatNumber(turno.documentadas)} doc ·{' '}
                            {formatNumber(turno.pendientesDocumento)} pend ·{' '}
                            {formatNumber(turno.errores)} err
                          </p>
                        </td>

                        <td className="py-3 pr-2 text-right align-top">
                          <p className="font-mono text-sm text-ink-900 tabular-nums">
                            {formatNumber(turno.boletas)} B · {formatNumber(turno.facturas)} F
                          </p>
                          <p className="mt-0.5 text-[11px] text-ink-400">
                            {formatNumber(turno.sinDespacho)} sin despacho
                          </p>
                        </td>

                        <td className="py-3 pr-2 text-right align-top">
                          <p className="font-mono text-sm text-ink-900 tabular-nums">
                            {formatNumber(turno.conCodigoSaldo)} ops
                          </p>
                          <p
                            className="mt-0.5 truncate text-[11px] text-ink-400"
                            title={formatCurrencyAdaptive(turno.saldoAplicado)}
                          >
                            {formatCurrencyAdaptive(turno.saldoAplicado)}
                          </p>
                        </td>

                        <td className="py-3 text-right align-top">
                          <p
                            className="truncate font-mono text-sm font-semibold text-ink-900 tabular-nums"
                            title={formatCurrencyAdaptive(turno.montoCobrado)}
                          >
                            {formatCurrencyAdaptive(turno.montoCobrado)}
                          </p>
                          <p
                            className="mt-0.5 truncate text-[11px] text-ink-400"
                            title={`Presupuestado ${formatCurrencyAdaptive(turno.montoPresupuestado)}`}
                          >
                            Presup. {formatCurrencyAdaptive(turno.montoPresupuestado)}
                          </p>
                        </td>
                      </tr>

                      {abierto ? (
                        <tr className="bg-ink-50/50">
                          <td colSpan={6} className="px-3 py-3">
                            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12px] sm:grid-cols-3 lg:grid-cols-4">
                              <div>
                                <dt className="text-ink-400">Llegada</dt>
                                <dd className="text-ink-800">{formatDateTimeValue(turno.hora_llegada)}</dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Salida</dt>
                                <dd className="text-ink-800">
                                  {turno.hora_salida === null
                                    ? 'En curso'
                                    : formatDateTimeValue(turno.hora_salida)}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Estado del turno</dt>
                                <dd className="text-ink-800">
                                  {etiquetaTurnoEstado(turno.turno_estado)} ({turno.turno_estado})
                                </dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Registros</dt>
                                <dd className="text-ink-800">{formatNumber(turno.registros)}</dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Documentadas</dt>
                                <dd className="text-ink-800">{formatNumber(turno.documentadas)}</dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Pendientes de documento</dt>
                                <dd className="text-ink-800">{formatNumber(turno.pendientesDocumento)}</dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Errores</dt>
                                <dd className="text-ink-800">{formatNumber(turno.errores)}</dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Sin despacho</dt>
                                <dd className="text-ink-800">{formatNumber(turno.sinDespacho)}</dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Boletas / facturas</dt>
                                <dd className="text-ink-800">
                                  {formatNumber(turno.boletas)} / {formatNumber(turno.facturas)}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Código de saldo</dt>
                                <dd className="text-ink-800">
                                  {formatNumber(turno.conCodigoSaldo)} ops ·{' '}
                                  {formatCurrencyAdaptive(turno.saldoAplicado)}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Presupuestado</dt>
                                <dd className="text-ink-800">
                                  {formatCurrencyAdaptive(turno.montoPresupuestado)}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-ink-400">Cobrado</dt>
                                <dd className="text-ink-800">{formatCurrencyAdaptive(turno.montoCobrado)}</dd>
                              </div>
                            </dl>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
